import { useCallback, useEffect, useState } from "react";
import Layout from "../../layout";
import { StepsProgress } from "./components/StepsProgress";
import { useStoryblok } from "../../hooks/useStoryblok";
import { calculateFieldsFormulas, calculateFormScoresBySections, getFieldScore } from "../../utils/score";
import {
  calculateFormPercentage,
  getFormErrors,
  handleRequiredFields,
} from "../../utils/form";
import { useToast } from "../../hooks/useToast";
import FirstStep from "./components/FirstStep";
import SecondStep from "./components/SecondStep";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  createNewCollection,
  getCollections,
  updateCollection,
} from "../../api";
import Button from "../../components/Button";
import { useSession } from "../../hooks/useSession";
import { CircularProgress } from "@mui/material";
import PageHeader from "../../components/PageHeader";
import Loading from "../../components/Loading";
import { clearScratchCollection } from "../../utils/scratch";
import _ from "lodash";

export const requiredFields = [
  "property",
  "historical",
  "area",
  "perimetro",
  "bairro",
  "cidade",
  "localizacao_praca",
  "date",
  "horario",
];

export default function NewAvaliacao() {
  const { id } = useParams();
  const [searchParams] = useSearchParams()
  const [values, setValues] = useState({});
  const [availableForms, setAvailableForms] = useState([]);
  const [secondForm, setSecondForm] = useState([]);
  const [activeSteps, setActiveSteps] = useState(1);
  const [completedPhases, setCompletedPhases] = useState([]);
  const [hasEmptyRequiredField, setHasEmptyRequiredField] = useState(true);
  const [isLoading, setLoading] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);

  const navigate = useNavigate();

  const fetchCollection = async () => {
    try {
      setLoading(true);
      const collectionsResponse = await getCollections();
      const collectionFound = collectionsResponse.find(
        (col: any) => col.id === Number(id)
      );
      if (collectionFound) {
        setValues(collectionFound.forms);
      } else {
        navigate('/home')
      }
      setLoading(false);
    } catch (error) {
      toast({
        message: "Não foi possível carregar esta avaliação.",
        type: "error",
      });
    }
  };

  const { toast } = useToast();

  const { cmsData } = useStoryblok();

  useEffect(() => {
    if (id) {
      fetchCollection();
    }
  }, [id]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeSteps]);

  useEffect(() => {
    const dirty = Object.keys(values).length > 3 && !id && !isSubmitting;
    const handler = (event: BeforeUnloadEvent) => {
      if (!dirty) {
        return;
      }
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [values, id, isSubmitting]);

  useEffect(() => {
    async function getFormValues() {
      const [completedPhases] = await Promise.all([
        localStorage.getItem("completedPhases"),
      ]);

      setCompletedPhases(completedPhases ? JSON.parse(completedPhases) : []);
    }

    getFormValues();
  }, []);

  useEffect(() => {
    if (cmsData && cmsData.forms) {
      setSecondForm(cmsData.forms as any);
      setAvailableForms(cmsData.forms as any);
    }
  }, [cmsData]);

  const calculatePercentage = async (values: any) => {
    const formPercentage = await calculateFormPercentage(values, secondForm);
    if (typeof formPercentage !== "undefined") {
      // setFormCompletionPercentage(formPercentage)
    }
  };

  useEffect(() => {
    calculatePercentage(values);

    if (activeSteps !== 3) {
      const validationResult = handleRequiredFields(requiredFields, values);

      const hasFalseValue = validationResult.some((item: any) => {
        return Object.values(item)[0] === false;
      });

      setHasEmptyRequiredField(hasFalseValue);
    }
  }, [values, secondForm, activeSteps]);

  const saveCollectionAsScratch = async (values: any) => {
    if (id || _.entries(values).length <= 3) {
      return
    }
    localStorage.setItem("scratch", JSON.stringify({ forms: values, scratch: true }))
  }

  const debounceSaveCollectionAsScratch = useCallback(
    _.debounce((values) => {
      saveCollectionAsScratch(values);
    }, 1000),
    [values]
  )

  useEffect(() => {
    debounceSaveCollectionAsScratch(values)

    return () => {
      debounceSaveCollectionAsScratch.cancel()
    }
  }, [values, debounceSaveCollectionAsScratch])

  useEffect(() => {
    const isScratch = searchParams.get('scratch')
    if (isScratch) {
      const scratchItem = localStorage.getItem('scratch')
      const scratch = scratchItem ? JSON.parse(scratchItem) : undefined
      if (scratch) {
        setLoading(true)
        setTimeout(() => {
          setValues(scratch.forms)
          setLoading(false)
        }, 500)
      }
    }
  }, [searchParams]);

  function getSelectedValores(score: any, data: any) {
    const { valores } = data;

    let value = 0;

    for (let i = 0; i < valores.length; i++) {
      const valor = valores[i];
      const startRange = valor.StartRange ? parseFloat(valor.StartRange) : null;
      const endRange = valor.EndRange ? parseFloat(valor.EndRange) : Infinity;

      if (startRange === null) {
        if (score > endRange) {
          value = valor;
        }
      } else {
        if (score >= startRange && score <= endRange) {
          value = valor;
        }
      }
    }

    return value;
  }

  const handleFormItem = (item: any, type: any) => {
    const updatedValues: any = { ...values, [type]: item };

    // We use these fields in formula calculation
    if (type === "area" || type === "perimetro") {
      updatedValues[`${type}.value`] = item.value;
      updatedValues[`${type}.metric`] = item.metric;
    }

    // Clear all fields that are dependent and are not visible
    const allFormFields = secondForm
      .map((form: any) => (form.campos ? form.campos : []))
      .flat();
    const fieldsWithDependency = allFormFields.filter(
      (campo) => campo.DependsOnFieldKey
    );

    for (const field of fieldsWithDependency) {
      const valueFound = updatedValues[field.DependsOnFieldKey];
      if (
        !field.DependsOnFieldValue ||
        (field.DependsOnFieldValue && !valueFound) ||
        updatedValues[field.DependsOnFieldKey] !== field.DependsOnFieldValue
      ) {
        delete updatedValues[field.chave];
      }
    }

    const fieldsAutomaticallyFilled = allFormFields.filter(
      (field) => field.component === "MultiplaescolhaAutomatica"
    );
    for (const field of fieldsAutomaticallyFilled) {
      const fieldToCheckFound = allFormFields.find(
        (currentField) => currentField.chave === field.FieldKeyToCheck
      );
      if (!fieldToCheckFound) {
        continue;
      }

      const fieldScore = getFieldScore(
        fieldToCheckFound.chave,
        allFormFields,
        updatedValues,
        true
      );
      if (fieldScore) {
        const optionToSelect: any = getSelectedValores(fieldScore, field);
        updatedValues[field.chave] = optionToSelect.nome;
      }
    }

    // Calculate formula fields
    calculateFieldsFormulas(allFormFields, updatedValues);

    setValues(updatedValues);

    if (type === "phase") {
      const filteredPhase: any = completedPhases.filter(
        (phase: any) =>
          phase.content?.Nome?.toUpperCase().trim() ===
          item.toUpperCase().trim()
      );

      const titlesToFilter = filteredPhase?.[0]?.content?.Secoes?.map(
        (item: any) => item?.content?.titulo
      );

      const filteredForms = availableForms.filter((form: any) =>
        titlesToFilter?.includes(form?.titulo)
      );

      const orderedForms: any = [];

      if (titlesToFilter) {
        for (const form of titlesToFilter) {
          const filteredForm = filteredForms.find((item: any) => {
            return item.titulo === form;
          });

          if (filteredForm) {
            orderedForms.push(filteredForm);
          }
        }
      }

      setSecondForm(orderedForms);
    }
  };

  const handleNextButton = () => {
    // if (!pageRender.secondPage) {
    //   setActiveSteps((prev) => ({ ...prev, step2: true }));
    //   ref.current?.scrollTo({ x: 0, y: 0, animated: true });
    //   return
    // }

    if (activeSteps === 2) {
      const error = getFormErrors(values, secondForm);

      if (error) {
        toast({
          message: `${error[0]} ${error[1]}`,
          type: "error",
        });
        return;
      }
    }

    setActiveSteps((previousStep) =>
      previousStep === 3 ? previousStep : previousStep + 1
    );
  };

  const handleBackButton = () => {
    setActiveSteps((previousStep) =>
      previousStep > 1 ? previousStep - 1 : previousStep
    );
    // ref.current?.scrollTo({ x: 0, y: 0, animated: true });
  };

  // const addPendingCollection = async () => {
  //   console.log("> Adicionando avaliação a lista de sincronizacao");

  //   const collections = JSON.parse(await AsyncStorage.getItem("collections")) ?? []

  //   const pendingPayloads = await AsyncStorage.getItem("collectionPayload")
  //     .then(parsePayload)
  //     .catch(() => []);

  //   await Promise.all([
  //     await AsyncStorage.setItem(
  //       "collectionPayload",
  //       JSON.stringify([...pendingPayloads, values])
  //     ),
  //     await AsyncStorage.setItem("collections", JSON.stringify(collections.filter(collection => !collection.scratch)))
  //   ])

  //   const count = await AsyncStorage.getItem("collectionPayload")
  //     .then(parsePayload)
  //     .then(({ length }) => length);

  //   console.log("> Avaliação armazenada no cache para sincronizar depois");
  //   console.log("> Total de avaliações pendentes: %d", count);

  //   return navigation.navigate("Feedback", {
  //     title: `Avaliação Pendente`,
  //     status: "success",
  //     type: "collection",
  //   });
  // };

  const { profile } = useSession();

  const submitCollection = async () => {
    if (isSubmitting) {
      return;
    }
    try {
      setSubmitting(true);
      
      const formScores = await calculateFormScoresBySections(
        values,
        availableForms
      );
  
      (values as any).scores = formScores;

      if (id) {
        await updateCollection(id, values, profile);
        navigate(`/avaliacoes/${id}`);
        toast({
          message: "Avaliação atualizada com sucesso",
          type: "success",
        });
      } else {
        const response = await createNewCollection(values, profile);
        if (!response?.id?.[0]) {
          throw new Error("Collection id missing");
        }
        clearScratchCollection();
        navigate(`/avaliacoes/${response.id[0]}`);
        toast({
          message: "Avaliação criada com sucesso",
          type: "success",
        });
      }
    } catch (error) {
      console.log(error);
      toast({
        message: `Não foi possível ${id ? "atualizar" : "salvar"} a avaliação. Tente novamente.`,
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <Loading label="Carregando formulário…" />
      </Layout>
    )
  }

  return (
    <Layout>
      <PageHeader
        title={id ? "Editar avaliação" : "Nova avaliação"}
        description={
          id
            ? "Atualize os dados da praça e dos indicadores e salve novamente."
            : "Cadastre a praça, preencha os indicadores e salve a coleta."
        }
      />
      <StepsProgress
        currentStep={activeSteps}
        steps={["Início", "Avaliação", "Conclusão"]}
      />

      <div className="pb-28">
      {activeSteps === 1 && (
        <FirstStep values={values} handleFormItem={handleFormItem} />
      )}

      {activeSteps === 2 && (
        <SecondStep
          values={values}
          handleFormItem={handleFormItem}
          secondForm={secondForm}
          setValues={setValues}
        />
      )}

      {activeSteps === 3 && (
        <div className="mx-auto my-10 max-w-xl rounded-2xl border border-border bg-white p-8 shadow-sm">
          <p className="text-pretty text-center text-xl font-bold text-gray">
            Pronto para salvar
          </p>
          <p className="mt-3 text-center leading-relaxed text-muted">
            Revise os dados da visita e finalize para gravar a coleta e abrir o
            resumo.
          </p>
          <dl className="mt-6 divide-y divide-border rounded-xl border border-border bg-surface/80">
            <div className="flex justify-between gap-4 px-4 py-3">
              <dt className="text-sm text-muted">Praça</dt>
              <dd className="text-right font-semibold text-gray">
                {(values as any).property || "Não informada"}
              </dd>
            </div>
            <div className="flex justify-between gap-4 px-4 py-3">
              <dt className="text-sm text-muted">Local</dt>
              <dd className="text-right font-semibold text-gray">
                {[
                  (values as any).bairro,
                  (values as any).cidade,
                ]
                  .filter(Boolean)
                  .join(" · ") || "Não informado"}
              </dd>
            </div>
            <div className="flex justify-between gap-4 px-4 py-3">
              <dt className="text-sm text-muted">Visita</dt>
              <dd className="text-right font-semibold text-gray">
                {(() => {
                  const raw = (values as any).date;
                  if (!raw) {
                    return "Não informada";
                  }
                  const date = new Date(raw);
                  if (Number.isNaN(date.getTime())) {
                    return "Não informada";
                  }
                  return new Intl.DateTimeFormat("pt-BR", {
                    dateStyle: "medium",
                  }).format(date);
                })()}
              </dd>
            </div>
          </dl>
        </div>
      )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-border bg-surface/95 px-4 py-4 backdrop-blur">
        <div className="mx-auto flex max-w-7xl gap-3">
          {activeSteps > 1 && (
            <Button
              variant="outlined"
              color="primary"
              className="w-1/2"
              onClick={handleBackButton}
            >
              Voltar
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            className={activeSteps > 1 ? "w-1/2" : "w-full"}
            onClick={activeSteps === 3 ? submitCollection : handleNextButton}
            disabled={
              (activeSteps !== 3 && hasEmptyRequiredField) || isSubmitting
            }
          >
            <span className="flex items-center justify-center gap-2">
              {isSubmitting && <CircularProgress color="inherit" size={20} />}
              {isSubmitting
                ? "Salvando…"
                : activeSteps === 3
                  ? "Finalizar"
                  : "Continuar"}
            </span>
          </Button>
        </div>
        {activeSteps === 1 && hasEmptyRequiredField && (
          <p className="mx-auto mt-2 max-w-7xl text-center text-sm text-muted">
            Preencha os campos obrigatórios para continuar.
          </p>
        )}
      </div>
    </Layout>
  );
}
